import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './index.module.scss';
import AgTable from '@navi/web-ui/lib/components/AgTable/AgTable';
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import { Button } from '@navi/web-ui/lib/primitives';
import SearchBarInput from '@navi/web-ui/lib/components/SearchBarInput';
import { CloseIcon, SearchIcon } from '@navi/web-ui/lib/icons';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { ALL_AGENTS_COLUMN_DEFS } from './TableContent';
import { RootState } from '@cp/src/store';
import Loader from 'src/components/Loader/Loader';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import { RowClickEvent } from '@cp/src/components/interfaces';
import EditFormDrawer from './AgentForm/components/drawer/EditFormDrawer';
import AddFormDrawer from './AgentForm/components/drawer/AddFormDrawer';
import { getAllAgents, getAllAgentsFilters } from './allAgentsAction';
import {
  FILTERS_TABLE_NAME,
  GridEvent,
  TABLE_NAME,
  defaultQueryParam,
  filterQueryParams,
  filtersParams,
  queryParam
} from './interface';
import FilterDrawer from '@cp/src/components/FilterDrawer';
import FilterSchema from './allAgentsFiltersSchema';
import { setAgentRefId, setIsDrawerOpen, setSearchValue } from '@cp/src/reducers/TeamLeadSlice';
import { handleTabClose } from '@cp/src/utils/commonUtils';
import HistoryIcon from '@cp/src/assets/icons/HistoryIcon';
import BulkUploaderDrawer from './BulkUpload/bulkUploadDrawer';
import UploadHistoryComponent from './UploadHistory/UploadHistoryDrawer';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { useColumnDefs } from './UploadHistory/ColumnDefs';
import { fetchUploadHistoryPolling } from './UploadHistory/actions/uploadHistoryDrawerActions';
import { Roles } from '../auth/constants/AuthConstants';
import { getAgentPreferences } from './AgentForm/actions/agentFormActions';

export const getQueryString = () => {
  const queryParams = readQueryParams();
  return queryParams[TABLE_NAME] as queryParam;
};

export const getFiltersQueryString = () => {
  const queryParams = readQueryParams();
  return queryParams[FILTERS_TABLE_NAME] as filtersParams;
};

export const filtersParamsModifier = (filters: filtersParams) => {
  return {
    ...(filters.agencyCode &&
      filters.agencyCode.length > 0 && {
        AGENCY_CODE: filters.agencyCode.split(',').map(item => ({ value: item }))
      }),
    ...(filters.agentRole &&
      filters.agentRole.length > 0 && {
        AGENT_ROLE: filters.agentRole.split(',').map(item => ({ value: item }))
      }),
    ...(filters.bucketGroup &&
      filters.bucketGroup.length > 0 && {
        BUCKET_GROUP: filters.bucketGroup.split(',').map(item => ({ value: item }))
      }),
    ...(filters.primaryLanguage &&
      filters.primaryLanguage.length > 0 && {
        PRIMARY_LANGUAGE: filters.primaryLanguage.split(',').map(item => ({ value: item }))
      }),
    ...(filters.agentState &&
      filters.agentState.length > 0 && {
        AGENT_STATE: filters.agentState.split(',').map(item => ({ value: item }))
      }),
    ...(filters.status &&
      filters.status.length > 0 && {
        STATUS: filters.status.split(',').map(item => ({ value: item }))
      })
  };
};

const AllAgents = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const gridEvent = useRef<any>(null);
  const [showLoader, setShowLoader] = useState(true);
  const tableRef = useRef(null);
  const allAgentsFiltersSchema = FilterSchema();
  const isCreateUserFeatureFlag = useSelector(
    (store: RootState) => store.common.featureFlags?.createUserFeatureFlag
  );

  const user = useSelector((state: RootState) => state.common.userData);

  const currentPageData = useSelector((state: RootState) => state.teamLeadDashboard.allAgentsData);
  const page = useSelector((state: RootState) => state.teamLeadDashboard.allAgentsPage);
  const loading = useSelector((state: RootState) => state.teamLeadDashboard.isLoading);
  const allAgentsPage = useSelector((state: RootState) => state.teamLeadDashboard.allAgentsPage);
  const isDrawerOpen = useSelector((state: RootState) => state.teamLeadDashboard.isDrawerOpen);
  const searchValue = useSelector((state: RootState) => state.teamLeadDashboard.searchValue);

  const [isEditable, setIsEditable] = useState(false);
  const [showAddUserDrawer, setShowAddUserDrawer] = useState(false);
  const [showEditUserDrawer, setShowEditUserDrawer] = useState(false);
  const [showUploadHistoryDrawer, setShowUploadHistoryDrawer] = useState(false);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);

  const isNumberManagementRole = user?.roles?.includes(Roles.ROLE_NUMBER_MANAGEMENT);

  const columnDefs = ALL_AGENTS_COLUMN_DEFS(() => {
    setIsEditable(true);
  });

  const handlerDrawerCTAClick = () => {
    setShowLoader(false);
    setShowAddUserDrawer(true);
    dispatch(setIsDrawerOpen(true));
  };

  const handleAddFormDrawerClosed = () => {
    dispatch(setIsDrawerOpen(false));
    setShowAddUserDrawer(false);
  };

  const handleEditFormDrawerClosed = () => {
    dispatch(setIsDrawerOpen(false));
    setShowEditUserDrawer(false);
    dispatch(setAgentRefId(''));
    setIsEditable(false);
  };

  const handleUploadHistoryClick = () => {
    setShowUploadHistoryDrawer(true);
  };

  const onCasesRowClicked = (event: RowClickEvent) => {
    const { data } = event;
    setShowLoader(true);
    setShowEditUserDrawer(true);
    dispatch(setIsDrawerOpen(true));
    dispatch(setAgentRefId(data?.referenceId));
  };

  useEffect(() => {
    dispatch(getAgentPreferences());
  }, []);

  useEffect(() => {
    isDrawerOpen
      ? window.addEventListener('beforeunload', handleTabClose)
      : window.removeEventListener('beforeunload', handleTabClose);
  }, [isDrawerOpen]);

  useEffect(() => {
    const queryString = getQueryString();
    if (!queryString) return;
    if (typeof gridEvent.current?.sizeColumnsToFit === 'function') {
      gridEvent.current?.sizeColumnsToFit();
    }
    tableRef?.current?.api?.sizeColumnsToFit();
  }, [location]);

  const getCurrentPageNumber = (): number => {
    const queryString = getQueryString();
    return Number(queryString?.pageNo) + 1 || 1;
  };

  const getPageSize = (): number => {
    const queryString = getQueryString();
    return Number(queryString?.pageSize) || 50;
  };

  const handleQueryChange = (query: string) => {
    const queryString = getQueryString();
    const filterQueryString = getFiltersQueryString();
    const payload = { ...queryString, pageNo: '0' };
    const filterPayload = { ...filterQueryString };
    const filtersPayload = filtersParamsModifier(filterPayload);

    if (query.length > 0) {
      payload.query = query;
    } else {
      delete payload.query;
    }
    updateAndNavigate(payload, filterPayload, filtersPayload);
  };

  const handleSearchChange = (searchValue: string) => {
    dispatch(setSearchValue(searchValue));
    if (searchValue.length === 0) {
      handleQueryChange('');
      return;
    } else if (searchValue.length < 3) {
      return;
    }
    handleQueryChange(searchValue);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const queryString = getQueryString();
    const filterQueryString = getFiltersQueryString();
    const payload: queryParam = {
      ...queryString,
      pageSize: pageSize.toString(),
      pageNo: '0'
    };
    const queryParams = createQueryParams({
      [TABLE_NAME]: payload,
      [FILTERS_TABLE_NAME]: { ...filterQueryString }
    });
    const filterPayload = { ...filterQueryString };
    const filtersPayload = filtersParamsModifier(filterPayload);
    dispatch(getAllAgents(payload, filtersPayload));
    navigate(queryParams);
  };

  const updateAndNavigate = (
    params: Record<string, Array<string>>,
    filterParams: Record<string, Array<string>>,
    filtersPayload: Record<string, Array<string>>
  ) => {
    const queryParams = createQueryParams({
      [TABLE_NAME]: { ...params, pageNo: '0' },
      [FILTERS_TABLE_NAME]: { ...filterParams }
    });

    dispatch(getAllAgents(params, filtersPayload));
    navigate(queryParams);
  };

  const handlePageChange = (pageNumber: number) => {
    const queryString = getQueryString();
    const filterQueryString = getFiltersQueryString();
    const payload = {
      ...queryString,
      pageNo: (pageNumber - 1).toString()
    };
    const queryParams = createQueryParams({
      [TABLE_NAME]: payload,
      [FILTERS_TABLE_NAME]: { ...filterQueryString }
    });
    const filterPayload = { ...filterQueryString };
    const filtersPayload = filtersParamsModifier(filterPayload);
    dispatch(getAllAgents(payload, filtersPayload));
    navigate(queryParams);
  };

  const handleFirstApiCall = () => {
    const queryString = getQueryString();
    const filtersQueryString = getFiltersQueryString();
    if (!user) return;
    let initialPayload: queryParam = {
      ...defaultQueryParam
    };
    if (!queryString) {
      const queryParams = createQueryParams({
        [TABLE_NAME]: initialPayload
      });
      navigate(queryParams);
    } else {
      initialPayload = {
        ...initialPayload,
        ...queryString
      };
    }
    if (filtersQueryString) {
      const filtersPayload: filterQueryParams = {
        AGENCY_CODE: filtersQueryString.agencyCode?.split(',')?.map(item => ({ value: item })),
        AGENT_ROLE: filtersQueryString.agentRole?.split(',')?.map(item => ({ value: item })),
        BUCKET_GROUP: filtersQueryString.bucketGroup?.split(',')?.map(item => ({ value: item })),
        PRIMARY_LANGUAGE: filtersQueryString.primaryLanguage
          ?.split(',')
          ?.map(item => ({ value: item })),
        AGENT_STATE: filtersQueryString.agentState?.split(',')?.map(item => ({ value: item })),
        STATUS: filtersQueryString.status?.split(',')?.map(item => ({ value: item }))
      };
      dispatch(getAllAgents(initialPayload, filtersPayload));
    } else {
      dispatch(getAllAgents(initialPayload));
    }
    dispatch(getAllAgentsFilters());
  };

  const onCasesGridReady = (event: GridEvent) => {
    handleFirstApiCall();
    const { api: gridApi } = event;
    gridEvent.current = gridApi;
    gridApi.sizeColumnsToFit();
    gridApi.getColumnDefs();
  };

  const handleOnApply = (selectedFilters: Record<string, string[]>) => {
    const selectedFiltersKeys: filterQueryParams = {
      ...(selectedFilters.agencyCode.length > 0 && {
        AGENCY_CODE: selectedFilters.agencyCode.map(item => ({ value: item }))
      }),
      ...(selectedFilters.agentRole.length > 0 && {
        AGENT_ROLE: selectedFilters.agentRole.map(item => ({ value: item }))
      }),
      ...(selectedFilters.bucketGroup.length > 0 && {
        BUCKET_GROUP: selectedFilters.bucketGroup.map(item => ({ value: item }))
      }),
      ...(selectedFilters.primaryLanguage.length > 0 && {
        PRIMARY_LANGUAGE: selectedFilters.primaryLanguage.map(item => ({ value: item }))
      }),
      ...(selectedFilters.agentState.length > 0 && {
        AGENT_STATE: selectedFilters.agentState.map(item => ({ value: item }))
      }),
      ...(selectedFilters.status.length > 0 && {
        STATUS: selectedFilters.status.map(item => ({ value: item }))
      })
    };
    const queryString = { ...getQueryString(), pageNo: '0' };
    const filterQueryString = getFiltersQueryString();
    const queryParams = createQueryParams({
      [TABLE_NAME]: queryString,
      [FILTERS_TABLE_NAME]: { ...filterQueryString }
    });
    dispatch(getAllAgents(queryString, selectedFiltersKeys));
    navigate(queryParams);
  };

  const handleReset = () => {
    dispatch(setSearchValue(''));
    handleSearchChange(''), ((searchBoxRef.current as HTMLInputElement | null) = null);
  };

  const onBulkUploadClick = () => {
    setShowUploadDrawer(true);
  };

  const { columnDefs: columnDefsUploadHistory } = useColumnDefs();
  const data = useSelector((store: RootState) => store?.uploadHistory?.data);
  const transformRowData = () => {
    return data?.map((item: GenericObject) => {
      return {
        originalFileExists: item.originalFileExists,
        type: item.type,
        successCount: item.successCount,
        failureCount: item.failureCount,
        startTime: item.startTime,
        owner: item.owner,
        status: item.status,
        referenceId: item.referenceId,
        originalFileName: item.originalFileName
      };
    });
  };

  return (
    <div className={styles.mainContainer}>
      <div className={'flex flex-row justify-between'}>
        User repository
        <div className={'flex flex-end gap-[16px]'}>
          {user?.naviUser ? (
            <>
              <Button
                variant="text"
                size="medium"
                startAdornment={<HistoryIcon />}
                className={'hover:underline'}
                onClick={handleUploadHistoryClick}
              >
                Upload history
              </Button>

              <Button
                variant="secondary"
                className={'border-msd px-[25px] py-[8px] bg-bg-primary'}
                onClick={onBulkUploadClick}
              >
                {'Bulk upload'}
              </Button>
            </>
          ) : null}
          <Button
            variant="primary"
            className={'border-md px-[52px] py-[8px]'}
            onClick={handlerDrawerCTAClick}
            disabled={!isCreateUserFeatureFlag}
          >
            Add user
          </Button>
        </div>
      </div>
      <div className={'flex justify-between align-center'}>
        <SearchBarInput
          ref={searchBoxRef}
          placeholder="Search here..."
          debounceDelay={300}
          inputSize={'medium'}
          containerClassName={'w-[190px] mt-[16px]'}
          value={searchValue || getQueryString()?.query || ''}
          onSearchChange={handleSearchChange}
          LeftInputAdornment={<SearchIcon style={{ cursor: 'text' }} />}
          RightInputAdornment={
            searchValue.length > 0 ? (
              <Button variant="text" onClick={handleReset}>
                <CloseIcon />
              </Button>
            ) : null
          }
        />
        <FilterDrawer
          filterButtonTitle="Filters"
          filterSchema={allAgentsFiltersSchema}
          selectedFiltersClass="pr-[0px] pt-[16px]"
          tableName={TABLE_NAME}
          onApply={handleOnApply}
        />
      </div>
      <AgTable
        ref={tableRef}
        defaultColDef={{
          suppressMovable: true,
          wrapText: true
        }}
        gridOptions={{ suppressCellSelection: true }}
        className="allAgentsTable"
        rowData={currentPageData || []}
        columnDefs={columnDefs}
        theme={'alpine'}
        rowClass={'cursor-pointer'}
        PaginationComponent={
          <Pagination
            key={currentPageData?.length}
            totalCount={page?.totalElements || 0}
            pageSize={getPageSize()}
            currentPage={getCurrentPageNumber()}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageNumberDropDownPosition={DropDownPosition.TOP}
          />
        }
        paginationWrapperClasses={'paginationWrapperClasses'}
        onGridReady={onCasesGridReady}
        animateRows={true}
        sizeColumnsToFit={true}
        onRowClicked={onCasesRowClicked}
      />
      {showAddUserDrawer && (
        <AddFormDrawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleAddFormDrawerClosed}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
        />
      )}
      {showEditUserDrawer && (
        <EditFormDrawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleEditFormDrawerClosed}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
        />
      )}
      <BulkUploaderDrawer
        showUploadDrawer={showUploadDrawer}
        setShowUploadDrawer={setShowUploadDrawer}
      />
      <UploadHistoryComponent
        showUploadHistoryComponent={showUploadHistoryDrawer}
        setShowUploadHistoryComponent={setShowUploadHistoryDrawer}
        data={data}
        transformRowData={transformRowData}
        columnDefs={columnDefsUploadHistory}
        fetchUploadHistoryPolling={fetchUploadHistoryPolling}
        headerText={'Upload history'}
      />
      <Loader show={loading} className={'overlay'} animate={false} />
    </div>
  );
};

export default AllAgents;
